import clsx from 'clsx';
import { ReactNode, useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Arrow } from 'components/icons';

import { addNonBreakingSpaces } from 'helpers/addNonBreakingSpaces';

import { LocaleContext } from '../Layout';
import { Link } from '../Link';
import { P } from '../Typography';
import { NewChip } from './NewChip';

export const SectionTitle = ({
  children,
  className,
  darkBg,
}: {
  children: ReactNode;
  className?: string;
  darkBg: boolean;
}) => {
  return (
    <p
      className={twMerge(
        clsx('text-sm font-bold uppercase text-grey-500 md:text-sm', darkBg && 'text-white', className),
      )}
    >
      {children}
    </p>
  );
};

export const NavLink = ({
  className,
  darkBg,
  item,
  onClick,
  title,
}: {
  className?: string;
  darkBg: boolean;
  item: { title: string; url: string; isNew?: boolean };
  onClick: () => void;
  title?: boolean;
}) => {
  return (
    <div className={twMerge('flex flex-wrap items-center gap-16 lg:justify-between lg:gap-8', className)} role="none">
      <Link
        variant="inverse"
        className="group flex w-fit items-center gap-4 hover:no-underline focus:text-danger-300 focus:no-underline"
        href={item.url}
        onClick={onClick}
        role="menuitem"
        id="nav-menu-item"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            document.getElementById('nav-tab')?.focus();
          }
        }}
      >
        {title ? (
          <SectionTitle
            className={twMerge(
              clsx(
                'text-primary-400 group-hover:text-primary-200 group-focus:text-primary-200 lg:text-grey-500',
                darkBg && 'text-white group-hover:text-secondary-75 group-focus:text-secondary-75 lg:text-white',
              ),
            )}
            darkBg={darkBg}
          >
            {item.title}
          </SectionTitle>
        ) : (
          <P
            className={twMerge(
              clsx(
                'text-sm font-medium text-primary-400 group-hover:text-primary-200 group-focus:text-primary-200 md:text-sm lg:text-grey-500',
                darkBg && 'text-white group-hover:text-secondary-75 group-focus:text-secondary-75 lg:text-white',
              ),
            )}
          >
            {item.title}
          </P>
        )}
        <Arrow
          className={twMerge(
            clsx(
              'h-16 w-16 text-grey-200 group-hover:text-primary-200 group-focus:text-primary-200',
              darkBg && 'group-hover:text-secondary-75 group-focus:text-secondary-75',
            ),
          )}
        />
      </Link>
      {item.isNew ? <NewChip darkBg={darkBg} /> : null}
    </div>
  );
};

export const NavDescription = ({
  children,
  className,
  darkBg,
}: {
  children: string;
  className?: string;
  darkBg: boolean;
}) => {
  const locale = useContext(LocaleContext);
  const descriptionText = locale === 'fr' ? addNonBreakingSpaces(children) : children;
  return (
    <P
      className={twMerge(clsx('text-sm text-grey-450 md:text-sm lg:max-w-180', darkBg && 'text-grey-100', className))}
      dangerouslySetInnerHTML={{ __html: descriptionText }}
    />
  );
};
